import { ComponentType } from 'react';
import { RouteObject } from 'react-router-dom';

import { EnumUserRole } from '../graphql';
import type { VendorModule as GraphQLModule } from '../graphql';

import {
  BaseModule,
  ModuleConfig,
  ModuleInstance,
  ModuleRouteCreator,
} from './base-module';

/**
 * Module Registry for managing and loading feature modules
 */
class ModuleRegistry {
  private modules: Map<string, BaseModule> = new Map();
  private activeModules: Map<string, ModuleInstance> = new Map();

  /**
   * Register a module with the registry
   * @param module
   */
  register(module: BaseModule): void {
    if (this.modules.has(module.id)) {
      return;
    }
    this.modules.set(module.id, module);
  }

  /**
   * Register multiple modules at once
   * @param modules
   */
  registerMany(modules: BaseModule[]): void {
    modules.forEach((module) => this.register(module));
  }

  /**
   * Get a registered module by ID
   * @param moduleId
   */
  get(moduleId: string): BaseModule | undefined {
    return this.modules.get(moduleId);
  }

  /**
   * Get all registered modules
   */
  getAll(): BaseModule[] {
    return Array.from(this.modules.values());
  }

  /**
   * Load active modules from user's vendor modules
   * @param vendorModules
   * @param userRole
   * @param _userRole
   */
  loadActiveModules(
    vendorModules: GraphQLModule[],
    _userRole: EnumUserRole,
  ): void {
    this.activeModules.clear();

    vendorModules.forEach((vendorModule) => {
      const registeredModule = this.modules.get(vendorModule.moduleType);

      if (!registeredModule) {
        return;
      }

      // Check if module is active
      if (!vendorModule.active) {
        return;
      }

      this.activeModules.set(vendorModule.moduleType, {
        config: registeredModule.getMetadata(),
        createRoutes: registeredModule.createRoutes.bind(registeredModule),
        isActive: true,
        settings: vendorModule.settings,
      });
    });
  }

  /**
   * Check if a module is active
   * @param moduleId
   */
  isModuleActive(moduleId: string): boolean {
    return this.activeModules.has(moduleId);
  }

  /**
   * Get active module instance
   * @param moduleId
   */
  getActiveModule(moduleId: string): ModuleInstance | undefined {
    return this.activeModules.get(moduleId);
  }

  /**
   * Get all active modules
   */
  getActiveModules(): ModuleInstance[] {
    return Array.from(this.activeModules.values());
  }

  /**
   * Generate routes for all registered modules (not just active ones)
   * This allows routes to exist even before modules are activated by the user
   * ModuleProtectedRoute will check if the module is actually active when accessed
   * @param ProtectedRoute
   * @param LazyWrapper
   * @param ModuleProtectedRoute
   */
  generateRoutes(
    ProtectedRoute: ComponentType<any>,
    LazyWrapper: ComponentType<any>,
    ModuleProtectedRoute?: ComponentType<any>,
  ): RouteObject[] {
    const routes: RouteObject[] = [];

    // Generate routes for all registered modules, not just active ones
    // The ModuleProtectedRoute component will check if the module is active
    this.modules.forEach((module) => {
      try {
        const moduleRoutes = module.createRoutes(
          ProtectedRoute,
          LazyWrapper,
          undefined,
          ModuleProtectedRoute,
        );
        routes.push(...moduleRoutes);
      } catch {
        // Skip routes for a module that fails to generate them rather than
        // breaking the whole app's routing.
      }
    });

    return routes;
  }

  /**
   * Clear all active modules
   */
  clearActiveModules(): void {
    this.activeModules.clear();
  }
}

// Create singleton instance
export const moduleRegistry = new ModuleRegistry();

// Export types and class
export { ModuleRegistry };
export type { ModuleConfig, ModuleInstance, ModuleRouteCreator };
